import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { donusumOnizleme } from '../../utils/birimDonusum';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const BIRIM_OPTIONS = ['KG', 'ADET', 'LT', 'PAKET', 'KOLI', 'PORSIYON'];
const trSayi = (n, d = 2) => Number(n || 0).toFixed(d).replace(/\.?0+$/, '').replace('.', ',');

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

const DogrudenMalKabulPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Master data
  const [tedarikciler, setTedarikçiler] = useState([]);
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [malzemeler, setMalzemeler] = useState([]);
  const [bdByMalzeme, setBdByMalzeme] = useState({});  // malzeme_id → [BirimDonusum]
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [tedarikçiId, setTedarikciId] = useState('');
  const [lokasyonId, setLokasyonId] = useState('');
  const [genelNot, setGenelNot] = useState('');
  const [satirlar, setSatirlar] = useState([
    { malzeme_id: '', miktar: '', birim_fiyat: '', not_kalem: '' }
  ]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = getAuthHeaders();
        const [tedRes, lokRes, malRes, bdRes] = await Promise.all([
          fetch(`${API_URL}/api/tedarikciler`, { headers }),
          fetch(`${API_URL}/api/lokasyonlar`, { headers }),
          fetch(`${API_URL}/api/malzemeler`, { headers }),
          fetch(`${API_URL}/api/birim-donusumleri`, { headers })
        ]);
        if (tedRes.ok) {
          const data = await tedRes.json();
          setTedarikçiler(data.filter(t => t.aktif !== false));
        }
        if (lokRes.ok) setLokasyonlar(await lokRes.json());
        if (malRes.ok) {
          const data = await malRes.json();
          setMalzemeler(data.filter(m => m.aktif !== false));
        }
        if (bdRes.ok) {
          const bds = await bdRes.json();
          const map = {};
          (Array.isArray(bds) ? bds : []).forEach(d => {
            (map[d.malzeme_id] = map[d.malzeme_id] || []).push(d);
          });
          setBdByMalzeme(map);
        }
      } catch (e) {
        setError('Veriler yüklenemedi');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const addSatir = () => {
    setSatirlar([...satirlar, { malzeme_id: '', miktar: '', birim_fiyat: '', not_kalem: '' }]);
  };

  const removeSatir = (index) => {
    if (satirlar.length <= 1) return;
    setSatirlar(satirlar.filter((_, i) => i !== index));
  };

  const updateSatir = (index, field, value) => {
    const updated = [...satirlar];
    updated[index] = { ...updated[index], [field]: value };
    // Malzeme seçilince birim + son fiyatı otomatik getir. Birim STOK biriminde
    // kalır — otomatik alış birimine (KOLI/PAKET) çevrilmez: geçmiş girişler kg
    // cinsinden yapıldığı için otomatik KOLI "ne yazarsan o kaydedilir" beklentisini
    // bozup sessiz double-conversion riski yaratıyordu. KOLI'yi kullanıcı bilerek
    // seçer; satırdaki canlı önizleme stok-birim sonucunu gösterir.
    if (field === 'malzeme_id' && value) {
      const mal = malzemeler.find(m => String(m.id) === String(value));
      if (mal) {
        updated[index].birim = mal.birim || 'KG';
        if (mal.fiyat && !updated[index].birim_fiyat) {
          updated[index].birim_fiyat = mal.fiyat;
        }
      }
    }
    setSatirlar(updated);
  };

  // Satır için kullanılabilir birim seçenekleri. BD varsa: tüm alış + stok birimleri
  // (dönüşümsüz yol için stok birimi de seçilebilir). BD yoksa: malzeme.birim.
  const getBirimOptions = (satir) => {
    if (!satir.malzeme_id) return BIRIM_OPTIONS;
    const bds = bdByMalzeme[parseInt(satir.malzeme_id, 10)];
    if (!Array.isArray(bds) || bds.length === 0) {
      const mal = malzemeler.find(m => String(m.id) === String(satir.malzeme_id));
      return mal?.birim ? [mal.birim] : BIRIM_OPTIONS;
    }
    const opts = new Set();
    bds.forEach(bd => { if (bd.stok_birimi) opts.add(bd.stok_birimi); if (bd.alis_birimi) opts.add(bd.alis_birimi); });
    return [...opts];
  };

  const toplamTutar = satirlar.reduce((sum, s) => {
    const miktar = parseFloat(s.miktar) || 0;
    const fiyat = parseFloat(s.birim_fiyat) || 0;
    return sum + (miktar * fiyat);
  }, 0);

  const toplamKg = satirlar.reduce((sum, s) => sum + (parseFloat(s.miktar) || 0), 0);

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (!tedarikçiId) { setError('Tedarikçi seçiniz'); return; }
    if (!lokasyonId) { setError('Lokasyon seçiniz'); return; }

    const validSatirlar = satirlar.filter(s => s.malzeme_id && parseFloat(s.miktar) > 0);
    if (validSatirlar.length === 0) { setError('En az bir malzeme satırı giriniz'); return; }

    setSubmitting(true);

    const body = {
      tedarikci_id: parseInt(tedarikçiId),
      teslim_alan_id: user.id,
      lokasyon_id: parseInt(lokasyonId),
      teslimat_notu: genelNot || null,
      kalemler: validSatirlar.map(s => ({
        malzeme_id: parseInt(s.malzeme_id),
        teslim_miktar: parseFloat(s.miktar),
        siparis_miktar: parseFloat(s.miktar),
        birim_fiyat: s.birim_fiyat ? parseFloat(s.birim_fiyat) : null,
        // Birim dönüşümünü backend bu alana göre uygular (alış birimiyse + BD stok
        // birimine çeviriyorsa). Stok biriminde girilirse dönüşüm olmaz (şişme yok).
        giris_birimi: s.birim || null,
        durum: 'OK',
        not_kalem: s.not_kalem || null
      }))
    };

    try {
      const res = await fetch(`${API_URL}/api/grn`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });

      if (res.ok) {
        const data = await res.json();
        setSuccess(`GRN ${data.grn_no} oluşturuldu! Kalite kontrol ve onay için yönlendiriliyorsunuz...`);
        setTimeout(() => navigate(`/grn/listesi`), 1500);
      } else {
        const err = await res.json();
        setError(err.detail || 'GRN oluşturulamadı');
      }
    } catch (e) {
      setError('Sunucu hatası');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && malzemeler.length === 0) {
    return (
      <div>
        <Header title="📦 Doğrudan Mal Kabul" />
        <div style={{ padding: '40px', textAlign: 'center', color: colors.textSecondary }}>Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: colors.background }}>
      <Header title="📦 Doğrudan Mal Kabul" />

      <div style={{ padding: '20px', maxWidth: '1100px', margin: '0 auto' }}>

        {/* Açıklama */}
        <div style={{
          background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: radius.md,
          padding: '12px 16px', marginBottom: '20px', fontSize: '13px', color: '#1565c0'
        }}>
          💡 Sipariş olmadan doğrudan tedarikçiden alınan ürünler için mal kabul fişi oluşturun.
          Oluşturulduktan sonra kalite kontrol ve onay işlemi yapılacaktır.
        </div>

        {error && (
          <div style={{
            background: '#f8d7da', color: '#721c24', padding: '10px 16px',
            borderRadius: radius.sm, marginBottom: '16px', fontSize: '13px'
          }}>❌ {error}</div>
        )}
        {success && (
          <div style={{
            background: '#d4edda', color: '#155724', padding: '10px 16px',
            borderRadius: radius.sm, marginBottom: '16px', fontSize: '13px'
          }}>✅ {success}</div>
        )}

        {/* Üst Form */}
        <div style={{
          background: colors.surface, borderRadius: radius.lg, padding: '20px',
          boxShadow: shadows.md, marginBottom: '20px'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: '#333' }}>Genel Bilgiler</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* Tedarikçi */}
            <div>
              <label style={labelStyle}>Tedarikçi *</label>
              <select
                value={tedarikçiId}
                onChange={e => setTedarikciId(e.target.value)}
                style={selectStyle}
              >
                <option value="">-- Tedarikçi Seçiniz --</option>
                {tedarikciler.map(t => (
                  <option key={t.id} value={t.id}>{t.unvan}</option>
                ))}
              </select>
            </div>

            {/* Lokasyon */}
            <div>
              <label style={labelStyle}>Teslim Lokasyonu *</label>
              <select
                value={lokasyonId}
                onChange={e => setLokasyonId(e.target.value)}
                style={selectStyle}
              >
                <option value="">-- Lokasyon Seçiniz --</option>
                {lokasyonlar.map(l => (
                  <option key={l.id} value={l.id}>{l.ad}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Not */}
          <div style={{ marginTop: '12px' }}>
            <label style={labelStyle}>Teslimat Notu</label>
            <textarea
              value={genelNot}
              onChange={e => setGenelNot(e.target.value)}
              placeholder="Genel not (opsiyonel)"
              style={{ ...inputStyle, height: '60px', resize: 'vertical' }}
            />
          </div>
        </div>

        {/* Malzeme Satırları */}
        <div style={{
          background: colors.surface, borderRadius: radius.lg, padding: '20px',
          boxShadow: shadows.md, marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>
              Malzeme Kalemleri ({satirlar.filter(s => s.malzeme_id).length} kalem)
            </h3>
            <button onClick={addSatir} style={addBtnStyle}>+ Satır Ekle</button>
          </div>

          {/* Tablo Başlığı */}
          <div style={{
            display: 'grid', gridTemplateColumns: '2fr 0.9fr 0.8fr 0.9fr 1.2fr 40px',
            gap: '8px', padding: '8px 4px', background: colors.backgroundAlt,
            borderRadius: radius.sm, marginBottom: '8px', fontSize: '12px',
            fontWeight: 'bold', color: '#555'
          }}>
            <div>Malzeme</div>
            <div>Miktar</div>
            <div>Birim</div>
            <div>Birim Fiyat (₺)</div>
            <div>Not</div>
            <div></div>
          </div>

          {/* Satırlar */}
          {satirlar.map((satir, index) => {
            const mal = malzemeler.find(m => String(m.id) === String(satir.malzeme_id));
            const onz = donusumOnizleme({
              miktar: parseFloat(satir.miktar) || 0,
              birim: satir.birim,
              netFiyat: parseFloat(satir.birim_fiyat) || 0,
              malzemeBirim: mal?.birim,
              bdList: bdByMalzeme[parseInt(satir.malzeme_id, 10)],
            });
            const hasBd = (bdByMalzeme[parseInt(satir.malzeme_id, 10)] || []).length > 0;
            return (
            <div key={index} style={{
              display: 'grid', gridTemplateColumns: '2fr 0.9fr 0.8fr 0.9fr 1.2fr 40px',
              gap: '8px', padding: '6px 4px', borderBottom: '1px solid #f0f0f0',
              alignItems: 'start'
            }}>
              <select
                value={satir.malzeme_id}
                onChange={e => updateSatir(index, 'malzeme_id', e.target.value)}
                style={{ ...selectStyle, fontSize: '13px', padding: '6px 8px' }}
              >
                <option value="">-- Malzeme --</option>
                {malzemeler.map(m => (
                  <option key={m.id} value={m.id}>{m.ad} ({m.birim || 'kg'})</option>
                ))}
              </select>

              <div>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={satir.miktar}
                  onChange={e => updateSatir(index, 'miktar', e.target.value)}
                  placeholder="0.0"
                  style={{ ...inputStyle, fontSize: '13px', padding: '6px 8px' }}
                />
                {onz && (
                  <div
                    data-testid={`mk-donusum-onizleme-${index}`}
                    title="Stok birimine çevrilmiş değer — kaydedilecek miktar/fiyat budur"
                    style={{ fontSize: '10px', marginTop: '3px', lineHeight: 1.25,
                             color: '#92400e', fontWeight: 600, whiteSpace: 'normal' }}
                  >
                    ≈ {trSayi(parseFloat(satir.miktar) || 0, 3)} {onz.alisBirim} × {trSayi(onz.katsayi, 3)} = <strong>{trSayi(onz.stokMiktar, 3)} {onz.stokBirim}</strong>
                    {onz.stokBirimFiyat != null && <> · {trSayi(onz.stokBirimFiyat, 2)} ₺/{onz.stokBirim}</>}
                  </div>
                )}
              </div>

              <select
                value={satir.birim || ''}
                onChange={e => updateSatir(index, 'birim', e.target.value)}
                style={{ ...selectStyle, fontSize: '13px', padding: '6px 8px', textAlign: 'center' }}
                title={hasBd ? 'Alış birimi — Birim Dönüşüm tanımlarından geliyor' : 'Birim Dönüşüm tanımı yok'}
              >
                {getBirimOptions(satir).map(b => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>

              <input
                type="number"
                step="0.01"
                min="0"
                value={satir.birim_fiyat}
                onChange={e => updateSatir(index, 'birim_fiyat', e.target.value)}
                placeholder="₺"
                style={{ ...inputStyle, fontSize: '13px', padding: '6px 8px' }}
              />

              <input
                type="text"
                value={satir.not_kalem}
                onChange={e => updateSatir(index, 'not_kalem', e.target.value)}
                placeholder="Not..."
                style={{ ...inputStyle, fontSize: '13px', padding: '6px 8px' }}
              />

              <button
                onClick={() => removeSatir(index)}
                disabled={satirlar.length <= 1}
                style={{
                  border: 'none', background: satirlar.length > 1 ? '#ffebee' : '#f5f5f5',
                  color: satirlar.length > 1 ? '#c62828' : '#ccc',
                  borderRadius: radius.sm, cursor: satirlar.length > 1 ? 'pointer' : 'default',
                  fontSize: '16px', padding: '4px'
                }}
                title="Satır sil"
              >🗑</button>
            </div>
            );
          })}
        </div>

        {/* Özet ve Submit */}
        <div style={{
          background: colors.surface, borderRadius: radius.lg, padding: '20px',
          boxShadow: shadows.md, marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', gap: '24px', fontSize: '14px' }}>
              <div>
                <span style={{ color: '#888' }}>Toplam: </span>
                <strong>{toplamKg.toFixed(1)} kg</strong>
              </div>
              {toplamTutar > 0 && (
                <div>
                  <span style={{ color: '#888' }}>Tutar: </span>
                  <strong style={{ color: styles.primary }}>
                    {toplamTutar.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                  </strong>
                </div>
              )}
              <div>
                <span style={{ color: '#888' }}>Kalem: </span>
                <strong>{satirlar.filter(s => s.malzeme_id).length}</strong>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => navigate('/grn/listesi')}
                style={{
                  padding: '10px 20px', border: `1px solid ${colors.border}`,
                  borderRadius: radius.md, background: colors.surface, cursor: 'pointer',
                  fontSize: '14px', color: '#555'
                }}
              >İptal</button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  padding: '10px 24px', border: 'none',
                  borderRadius: radius.md, cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '14px', fontWeight: 'bold', color: 'white',
                  background: submitting
                    ? '#999'
                    : `linear-gradient(135deg, ${styles.primary}, ${styles.primaryDark})`,
                  boxShadow: '0 2px 6px rgba(211,47,47,0.3)'
                }}
              >
                {submitting ? '⏳ Kaydediliyor...' : '📦 Mal Kabul Oluştur'}
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

// Shared styles
const labelStyle = {
  display: 'block', fontSize: '12px', fontWeight: '600',
  color: '#555', marginBottom: '4px'
};

const inputStyle = {
  width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`,
  borderRadius: radius.sm, fontSize: '14px', outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s'
};

const selectStyle = {
  ...inputStyle, background: colors.surface, cursor: 'pointer'
};

const addBtnStyle = {
  padding: '6px 14px', border: `1px solid ${styles.primary}`,
  borderRadius: radius.sm, background: colors.surface, color: styles.primary,
  cursor: 'pointer', fontSize: '13px', fontWeight: '600'
};

export default DogrudenMalKabulPage;
